import axios from 'axios';
import {ENDPOINT_URL, GET_MAC_ACL_LIST} from '../../../constants/commonConstants';
import {
	decodeAgentResponse,
	LOOKUP_LIMIT,
	optionalText,
	resolveCandidatesWithFallback,
	responseRows,
} from '../../core/resolutionUtils';
import {
	getResolvedEntity,
	replaceResolvedEntities,
} from '../../core/resolvedEntityStore';

/**
 * Return a Access Point MAC ACL row identifier.
 *
 * The AP MAC ACL table carries a numeric acl_id.
 *
 * @param {object} item Canvas row
 * @return {*} identifier
 */
function rowId(item) {
	return item.acl_id ?? item.id;
}

/**
 * Normalize a Canvas Access Point MAC ACL row.
 * @param {object} item Canvas row
 * @return {object|null} agent candidate
 */
function normalizeRow(item) {
	const id = rowId(item);
	const name = item.acl_name ?? item.name;
	if (id == null || !name) return null;
	return {
		entity_type: 'ap_mac_acl',
		entity_id: String(id),
		name: String(name),
		status: optionalText(item.status),
		site_id: null,
		site_name: null,
		serial_no: null,
	};
}

/**
 * Resolve Access Point MAC ACL candidates.
 * @param {object} params resolver parameters
 * @return {Promise<{candidates: object[]}>} normalized candidates
 */
export async function resolveApMacAcl(params) {
	const query = String(params.query || '').trim();
	const browse = params.browse === true;
	if (!query && !browse) throw new Error('lookup_query_required');
	const loadRows = async (search, limit) => {
		const searchParam = search
			? `&search=${encodeURIComponent(search)}`
			: '';
		const url = `${ENDPOINT_URL}${GET_MAC_ACL_LIST}?limit=${limit}` +
			`&offset=0${searchParam}`;
		const {data} = await axios.get(url);
		return responseRows(decodeAgentResponse(data) || {});
	};
	if (!query) {
		const limit = Math.min(
			Math.max(Number(params.limit) || LOOKUP_LIMIT, 1),
			LOOKUP_LIMIT,
		);
		const rows = await loadRows(null, limit);
		replaceResolvedEntities('ap_mac_acl', rows, rowId);
		return {
			candidates: rows.map(normalizeRow).filter(Boolean).slice(0, limit),
		};
	}
	const {rows, candidates} = await resolveCandidatesWithFallback({
		query,
		loadRows,
		normalizeRow,
		fuzzy: params.fuzzy === true,
	});
	replaceResolvedEntities('ap_mac_acl', rows, rowId);
	return {candidates};
}

/**
 * Return a retained Access Point MAC ACL row.
 * @param {string} entityId entity identifier
 * @return {object|null} Canvas row
 */
export function getResolvedApMacAcl(entityId) {
	return getResolvedEntity('ap_mac_acl', entityId);
}
